import { buildLineKey } from './documentLineItems';
import { isFormatOfferPrice } from './formatOffers';
import { calculateClientPricingMarginDiscount } from './clientPricingMatrix';

export const computeClientPricingDiscountAmount = (price, discount) => {
  if (discount?.candidateRules?.length) {
    return Math.max(...discount.candidateRules.map((rule) => computeClientPricingDiscountAmount(price, { ...rule, referenceCost: rule.referenceCost ?? discount.referenceCost })));
  }
  const basePrice = Math.max(0, Math.floor(Number(price || 0)));
  if (!discount || basePrice <= 0) return 0;
  const isMarginRule = discount.ruleType === 'margin_discount'
    || (discount.marginReductionPercent !== null && discount.marginReductionPercent !== undefined);
  if (isMarginRule) {
    const resolvedDiscountAmount = Number(discount.discountAmount);
    const resolvedBasePrice = Number(discount.basePrice);
    if (Number.isFinite(resolvedDiscountAmount) && resolvedBasePrice === basePrice) {
      return Math.min(Math.max(Math.floor(resolvedDiscountAmount), 0), basePrice);
    }
    const referenceCostValue = discount.referenceCost?.referenceCostRaw
      ?? discount.referenceCost?.referenceCost
      ?? discount.referenceCostRaw ?? discount.referenceCostValue;
    const reductionPercent = Number(discount.marginReductionPercent);
    if (!Number.isFinite(reductionPercent) || reductionPercent <= 0) return 0;
    return calculateClientPricingMarginDiscount({
      basePrice,
      referenceCost: referenceCostValue,
      marginReductionPercent: reductionPercent,
    }).discountAmount;
  }
  const value = Math.max(0, Number(discount.discountValue || 0));
  const raw = discount.discountType === 'percent'
    ? Math.floor(basePrice * (value / 100))
    : Math.floor(value);
  return Math.min(Math.max(raw, 0), basePrice);
};

export const resolveClientPricingDiscount = (price, discount) => {
  if (!discount?.candidateRules?.length) return discount;
  const candidates = discount.candidateRules.map((rule) => ({ ...rule, referenceCost: rule.referenceCost ?? discount.referenceCost }));
  const winner = candidates.sort((a, b) => computeClientPricingDiscountAmount(price, b) - computeClientPricingDiscountAmount(price, a)
    || Number(a.clientPricingCategoryId) - Number(b.clientPricingCategoryId))[0];
  return { ...winner, candidateRules: discount.candidateRules };
};

const metaPatchChanged = (meta, patch) => (
  Object.entries(patch).some(([key, value]) => meta?.[key] !== value)
);

const stripLegacyPricingLimitMeta = (meta = {}) => {
  const legacyKeys = [
    'clientPricingMinimumProfitType',
    'clientPricingMinimumProfitValue',
    'clientPricingMaxDiscountType',
    'clientPricingMaxDiscountValue',
    'globalMaxDiscountType',
    'globalMaxDiscountValue',
    'effectiveMaxDiscountAmount',
    'clientPricingBypassDiscountLimits',
    'globalDiscountCap',
    'effectiveDiscountCap',
    'explicitMaxDiscount',
    'effectiveMaxDiscount',
    'marginDerivedMaxDiscount',
    'discountCap',
    'discountLimit',
    'discountWarning',
    'discountWarnings',
    'discountExceedsMax',
  ];
  if (!legacyKeys.some((key) => Object.hasOwn(meta, key))) return meta;
  const next = { ...meta };
  legacyKeys.forEach((key) => delete next[key]);
  return next;
};

const cloneMatrix = (matrix = {}) => {
  const next = {};
  Object.keys(matrix || {}).forEach((key) => {
    next[key] = Array.isArray(matrix[key]) ? [...matrix[key]] : [];
  });
  return next;
};

const normalizeDiscountMap = (discountsByVarietyPriceId = {}) => {
  if (discountsByVarietyPriceId instanceof Map) return discountsByVarietyPriceId;
  return new Map(
    Object.entries(discountsByVarietyPriceId || {})
      .map(([key, value]) => [Number(key), value])
      .filter(([key]) => Number.isInteger(key))
  );
};

export const applyClientPricingDiscountsToMatrices = ({
  products = [],
  discounts = {},
  linePriceMeta = {},
  discountsByVarietyPriceId = {},
}) => {
  const discountMap = normalizeDiscountMap(discountsByVarietyPriceId);
  const nextDiscounts = cloneMatrix(discounts);
  const nextLinePriceMeta = { ...(linePriceMeta || {}) };
  let changed = false;

  (products || []).forEach((product) => {
    (product.Varieties || []).forEach((variety) => {
      (variety.VarietyPrices || []).forEach((price, priceIndex) => {
        const key = buildLineKey(variety.id, priceIndex);
        if (isFormatOfferPrice(price)) {
          if (!nextDiscounts[variety.id]) nextDiscounts[variety.id] = [];
          if (nextDiscounts[variety.id][priceIndex] !== 0 || nextLinePriceMeta[key]?.discountSource !== 'format_offer') {
            nextDiscounts[variety.id][priceIndex] = 0;
            nextLinePriceMeta[key] = { ...nextLinePriceMeta[key], discountSource: 'format_offer' };
            changed = true;
          }
          return;
        }
        const originalMeta = nextLinePriceMeta[key] || {};
        const meta = stripLegacyPricingLimitMeta(originalMeta);
        if (meta !== originalMeta) {
          nextLinePriceMeta[key] = meta;
          changed = true;
        }
        const currentDiscount = Number(nextDiscounts[variety.id]?.[priceIndex] || 0);
        const storedManualCandidate = Number(meta.manualDiscountCandidate);
        const manualDiscountCandidate = meta.discountSource === 'manual'
          ? Math.max(currentDiscount, 0)
          : Number.isFinite(storedManualCandidate)
            ? Math.max(storedManualCandidate, 0)
            : 0;
        const entry = resolveClientPricingDiscount(price?.price, discountMap.get(Number(price.id)));
        const categoryDiscount = computeClientPricingDiscountAmount(price?.price, entry);
        if (!nextDiscounts[variety.id]) nextDiscounts[variety.id] = [];

        if (!entry) {
          const restoresManual = meta.discountSource === 'client_category'
            && manualDiscountCandidate > 0;
          const nextDiscount = meta.discountSource === 'client_category' ? manualDiscountCandidate : currentDiscount;
          const nextMeta = {
            ...meta,
            manualDiscountCandidate: manualDiscountCandidate || null,
            clientPricingCandidateRules: null,
            clientPricingDiscountType: null,
            clientPricingDiscountValue: null,
            clientPricingRuleType: null,
            clientPricingMarginReductionPercent: null,
            clientPricingReferenceCost: null,
            clientPricingSnapshot: null,
            clientPricingCalculationStatus: null,
            clientPricingCategoryId: null,
            clientPricingCategoryName: null,
            ...(meta.discountSource === 'client_category'
              ? { discountSource: restoresManual ? 'manual' : 'none' }
              : {}),
          };
          if (metaPatchChanged(meta, nextMeta)) {
            nextLinePriceMeta[key] = nextMeta;
            changed = true;
          }
          if (meta.discountSource === 'client_category' && currentDiscount !== nextDiscount) {
            nextDiscounts[variety.id][priceIndex] = nextDiscount;
            changed = true;
          }
          return;
        }

        const manualWins = manualDiscountCandidate > categoryDiscount;
        const nextDiscount = manualWins ? manualDiscountCandidate : categoryDiscount;
        const nextMeta = {
          ...meta,
          manualDiscountCandidate: manualDiscountCandidate || null,
          discountSource: manualWins
            ? 'manual'
            : 'client_category',
          clientPricingCandidateRules: entry.candidateRules || null,
          clientPricingSnapshot: entry.clientPricingSnapshot || null,
          clientPricingCategoryId: entry.clientPricingCategoryId || null,
          clientPricingCategoryName: entry.clientPricingCategoryName || null,
          clientPricingDiscountType: entry.discountType || null,
          clientPricingDiscountValue: entry.discountValue ?? null,
          clientPricingRuleType: entry.ruleType || 'fixed_discount',
          clientPricingMarginReductionPercent: entry.marginReductionPercent ?? null,
          clientPricingReferenceCost: entry.referenceCost?.referenceCostRaw
            ?? entry.referenceCost?.referenceCost
            ?? entry.referenceCostRaw
            ?? null,
          clientPricingCalculationStatus: manualWins
            ? 'overridden_by_manual'
            : entry.calculationStatus || (categoryDiscount > 0 ? null : 'zero_discount'),
        };
        if (metaPatchChanged(meta, nextMeta)) {
          nextLinePriceMeta[key] = nextMeta;
          changed = true;
        }
        if (currentDiscount !== nextDiscount) {
          nextDiscounts[variety.id][priceIndex] = nextDiscount;
          changed = true;
        }
      });
    });
  });

  return { discounts: nextDiscounts, linePriceMeta: nextLinePriceMeta, changed };
};
